import { useSyncExternalStore } from 'react'

import type {
  AgentPenSpec,
  ChartBand,
  ChatPart,
  ChatTurn,
  FocusRequest,
} from './chatStream'

/**
 * What the agent has asked the chart to look like.
 *
 * Separate from `modelCard` on purpose. A model owns the pens that make it up
 * and takes them back when it closes; these belong to the conversation — a
 * rolling average someone asked for, the window a finding is about — and
 * neither saving a model nor closing one should disturb them.
 *
 * A module store rather than component state for the same reason the model card
 * is one: hiding the right-hand panel unmounts the Agent tab, and hiding the
 * panel is exactly what you do to look at the chart.
 */

/** An ad-hoc pen the agent put up to make a point. */
export interface AgentPen {
  id: string
  name: string
  unit: string
  spec: AgentPenSpec
}

export interface AgentViewState {
  pens: AgentPen[]
  /** The tinted span, until the agent replaces it or the user closes it. */
  band: ChartBand | null
  /**
   * A chart move waiting to be made. One-shot rather than desired state: after
   * the agent moves the window the user is free to pan somewhere else, and a
   * declarative focus would drag them back on the next unrelated render.
   */
  focus: (FocusRequest & { nonce: number }) | null
}

export const EMPTY_VIEW: AgentViewState = { pens: [], band: null, focus: null }

let state: AgentViewState = EMPTY_VIEW
let nonce = 0
const listeners = new Set<() => void>()

const set = (next: AgentViewState): void => {
  state = next
  listeners.forEach((listener) => listener())
}

export const subscribeAgentView = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const agentViewState = (): AgentViewState => state

export const useAgentViewState = (): AgentViewState =>
  useSyncExternalStore(subscribeAgentView, agentViewState, agentViewState)

/**
 * Fold a streamed part into the view. Called for LIVE frames only: reopening an
 * old conversation should show what the agent did, not redo it to the chart the
 * user is currently looking at.
 */
export const applyChartPart = (part: ChatPart): void => {
  if (part.kind === 'focus') {
    nonce += 1
    set({
      ...state,
      // A focus carrying no band leaves the previous one alone; the agent
      // clears it by banding something else, and the user by closing it.
      band: part.band ?? state.band,
      focus: { ...part, nonce },
    })
    return
  }
  if (part.kind !== 'pen') return
  if (part.op === 'remove') {
    set({ ...state, pens: state.pens.filter((pen) => pen.id !== part.penId) })
    return
  }
  if (!part.spec) return
  const pen: AgentPen = {
    id: part.penId,
    name: part.name,
    unit: part.unit ?? '',
    spec: part.spec,
  }
  set({
    ...state,
    pens: [...state.pens.filter((existing) => existing.id !== pen.id), pen],
  })
}

/** The user closing the band. Deliberately not something the agent can undo. */
export const dismissBand = (): void => {
  if (!state.band) return
  set({ ...state, band: null })
}

export const removeAgentPen = (penId: string): void => {
  if (!state.pens.some((pen) => pen.id === penId)) return
  set({ ...state, pens: state.pens.filter((pen) => pen.id !== penId) })
}

/** Opening another conversation, or switching site: none of this applies there. */
export const clearAgentView = (): void => {
  if (state === EMPTY_VIEW) return
  set(EMPTY_VIEW)
}

/**
 * Rebuild the pens of a conversation being reopened.
 *
 * A pen belongs to the chat that asked for it, so it should still be there
 * tomorrow. The transcript already records every add and remove, so replaying
 * those parts reconstructs the set without storing it twice — and it cannot
 * drift from what the conversation says, because it is what the conversation
 * says.
 *
 * Deliberately pens only. The window and the band are not restored: opening a
 * chat to read it should not move the chart someone is working on.
 */
export const restoreAgentPens = (turns: ChatTurn[]): void => {
  const pens = new Map<string, AgentPen>()
  for (const turn of turns) {
    for (const part of turn.parts) {
      if (part.kind !== 'pen') continue
      if (part.op === 'remove') {
        pens.delete(part.penId)
      } else if (part.spec) {
        pens.set(part.penId, {
          id: part.penId,
          name: part.name,
          unit: part.unit ?? '',
          spec: part.spec,
        })
      }
    }
  }
  set({ ...EMPTY_VIEW, pens: [...pens.values()] })
}
